import { Button, Input, Popconfirm, Space, Table, Tag } from 'antd';
import {
  useCreateEmployee,
  useEmployees,
  useToggleEmployeeState,
  useUpdateEmployee,
} from '../../utils/api-hooks/employee.hook';
import { EditOutlined, UserSwitchOutlined } from '@ant-design/icons';
import useModal from 'antd/es/modal/useModal';
import EmployeeFormComponent from './employees-form.component';
import { useCallback, useRef, useState } from 'react';
import { FormInstance } from 'antd/lib';
import { usePagination } from '../../shared/usePagination';
import moment from 'moment';
import {
  EMPLOYEE_STATE,
  EMPLOYEE_STATE_COLOR,
  EMPLOYEE_STATE_LABEL,
} from '../../constants/employee';
import { debounce, map } from 'lodash';
import { Link } from 'react-router-dom';
import AddEmployeeFormComponent from '../departments/department/add-employee-form.component';
import { useAddDepartmentEmployee } from '../../utils/api-hooks/department.hook';
import { ToggleEmployeeStateDtoStateEnum } from '../../utils/api-client';
import dayjs from 'dayjs';

export default function EmployeesContainer() {
  const [modal, contextHolder] = useModal();
  const createFormRef = useRef<FormInstance>(null);
  const editFormRef = useRef<FormInstance>(null);
  const addEmployeeFormRef = useRef<FormInstance>(null);
  const pagination = usePagination();
  const [keyword, setKeyword] = useState<string>('');
  const { data: { data: employees, total } = {}, isLoading } = useEmployees({
    take: pagination.take,
    skip: pagination.skip,
    keyword,
  });
  const { mutateAsync: createEmployee } = useCreateEmployee();
  const { mutateAsync: updateEmployee } = useUpdateEmployee();
  const { mutateAsync: toggleEmployeeState } = useToggleEmployeeState();
  const { mutateAsync: addDepartmentEmployee } = useAddDepartmentEmployee();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onKeywordChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      setKeyword(e.target.value);
    }, 800),
    []
  );

  const onCreateEmployeeButtonClick = async () => {
    let id: string;

    modal
      .confirm({
        title: '新增員工',
        content: <EmployeeFormComponent formRef={createFormRef} />,
        icon: null,
        async onOk() {
          const values = await createFormRef.current!.validateFields();

          const res = await createEmployee(values);

          id = res.data.id;
        },
      })
      .then(
        (res) => {
          if (!res) {
            return;
          }

          modal.confirm({
            title: '加入部門',
            content: (
              <AddEmployeeFormComponent
                formRef={addEmployeeFormRef}
                employeeId={id}
                isAfterEmployeeCreated
              />
            ),
            icon: null,
            async onOk(...args) {
              const values = await addEmployeeFormRef.current!.validateFields();

              await addDepartmentEmployee(values);
            },
          });
        },
        () => {}
      );
  };

  const onEditEmployeeButtonClick = (record: any) => {
    modal.confirm({
      title: '編輯員工',
      content: (
        <EmployeeFormComponent
          formRef={editFormRef}
          initialValues={{
            name: record.name,
            phone: record.phone,
            birthday: record.birthday ? dayjs(record.birthday) : undefined,
            idNumber: record.idNumber,
            nationalIdNumber: record.nationalIdNumber,
            nickname: record.nickname,
            email: record.email,
            onBoardingTime: dayjs(record.onBoardingTime),
            type: record.type,
          }}
        />
      ),
      icon: null,
      async onOk() {
        const values = await editFormRef.current!.validateFields();

        await updateEmployee({
          id: record.id,
          ...values,
        });
      },
    });
  };

  const handleToggleEmployeeState = async (
    id: string,
    currentState: string
  ) => {
    const newState =
      currentState === EMPLOYEE_STATE.ACTIVATED
        ? ToggleEmployeeStateDtoStateEnum.Suspended
        : ToggleEmployeeStateDtoStateEnum.Activated;

    await toggleEmployeeState({
      id,
      state: newState,
    });
  };

  return (
    <Space direction="vertical" className="w-full">
      {contextHolder}
      <div className="text-2xl font-bold">員工管理</div>
      <Table
        dataSource={employees}
        rowKey="id"
        size="small"
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        pagination={{
          ...pagination,
          total,
        }}
        title={() => (
          <div className="flex flex-col items-start gap-2">
            <Button type="primary" onClick={onCreateEmployeeButtonClick}>
              新增員工
            </Button>
            <div className="flex flex-row items-start gap-2">
              <div className="flex items-center gap-4">
                <span className="shrink-0">關鍵字</span>
                <Input
                  placeholder="輸入姓名、綽號或電話號碼進行搜尋"
                  onChange={onKeywordChange}
                />
              </div>
            </div>
          </div>
        )}
        columns={[
          {
            align: 'center',
            dataIndex: 'state',
            title: '狀態',
            width: 70,
            render(value) {
              return (
                <span className={EMPLOYEE_STATE_COLOR[value as EMPLOYEE_STATE]}>
                  {EMPLOYEE_STATE_LABEL[value as EMPLOYEE_STATE]}
                </span>
              );
            },
          },
          {
            align: 'center',
            dataIndex: 'idNumber',
            title: '員工編號',
            width: 80,
            render(value) {
              return value || '---';
            },
          },
          {
            align: 'center',
            dataIndex: 'onBoardingTime',
            title: '入職時間',
            width: 120,
            render(value) {
              return moment(value).utcOffset(8).format('YYYY/MM/DD');
            },
          },
          {
            title: '姓名',
            width: 120,
            dataIndex: 'name',
          },
          {
            title: '綽號',
            width: 120,
            dataIndex: 'nickname',
          },
          {
            dataIndex: 'birthday',
            title: '生日',
            width: 120,
            render(value) {
              return moment(value).utcOffset(8).format('YYYY/MM/DD');
            },
          },
          {
            title: '電話號碼',
            width: 120,
            dataIndex: 'phone',
          },
          {
            title: '電子郵件',
            dataIndex: 'email',
            width: 250,
            render(value) {
              return value || '---';
            },
          },
          {
            title: '部門',
            dataIndex: 'departments',
            render(value) {
              return map(value, (department) => (
                <Link
                  to={`/human-resource/departments/${department.id}`}
                  key={department.id}
                >
                  <Tag>{department.name}</Tag>
                </Link>
              ));
            },
          },
          {
            title: '操作',
            width: 120,
            dataIndex: 'operation',
            render(_, record) {
              return (
                <Space>
                  <Button
                    icon={<EditOutlined />}
                    shape="circle"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditEmployeeButtonClick(record);
                    }}
                  />
                  <Popconfirm
                    title={`確定要將員工狀態從「${EMPLOYEE_STATE_LABEL[record.state as EMPLOYEE_STATE]}」切換為「${EMPLOYEE_STATE_LABEL[record.state === EMPLOYEE_STATE.ACTIVATED ? EMPLOYEE_STATE.SUSPENDED : EMPLOYEE_STATE.ACTIVATED]}」嗎？`}
                    onConfirm={() =>
                      handleToggleEmployeeState(record.id, record.state)
                    }
                    okText="確定"
                    cancelText="取消"
                  >
                    <Button
                      icon={<UserSwitchOutlined />}
                      shape="circle"
                      size="small"
                      type={
                        record.state === EMPLOYEE_STATE.ACTIVATED
                          ? 'default'
                          : 'primary'
                      }
                    />
                  </Popconfirm>
                </Space>
              );
            },
          },
        ]}
      />
    </Space>
  );
}
